import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { CreditCard, Landmark, Wallet, ChevronRight, Lock, Check } from 'lucide-react';
import { toast } from 'sonner';
import IyzicoPaymentModal from '../components/IyzicoPaymentModal';
import LocationPickerMap from '../components/LocationPickerMap';
import AddressesAPI from '../utils/addressesAPI';
import LocationsAPI from '../utils/locationsAPI';

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, getShippingCost, getGrandTotal, clearCart } = useCart();
  const { isAuthenticated, user } = useAuth();

  const [step, setStep] = useState(1); // 1: Adres, 2: Ödeme, 3: Onay
  const [paymentMethod, setPaymentMethod] = useState('credit-card');
  const [selectedAddress, setSelectedAddress] = useState('');
  const [billingAddress, setBillingAddress] = useState('same');
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentId, setPaymentId] = useState(null);
  const [showIyzicoModal, setShowIyzicoModal] = useState(false);
  const [iyzicoOrderData, setIyzicoOrderData] = useState(null);

  const [savedAddresses, setSavedAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(true);

  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);

  // SECURITY: Card info removed from frontend
  // Payment will be processed through payment gateway
  // Card data never stored in React state

  const [newAddress, setNewAddress] = useState({
    title: 'Ev',
    name: '',
    phone: '',
    city_id: '',
    city_name: '',
    district_id: '',
    district_name: '',
    neighborhood_id: '',
    neighborhood_name: '',
    address_line: '',
    latitude: null,
    longitude: null,
  });

  const selectedSavedAddress = useMemo(() => {
    if (!selectedAddress || selectedAddress === 'new') return null;
    return savedAddresses.find((a) => String(a.id) === String(selectedAddress)) || null;
  }, [savedAddresses, selectedAddress]);

  useEffect(() => {
    const load = async () => {
      if (!isAuthenticated) {
        setSavedAddresses([]);
        setAddressesLoading(false);
        return;
      }
      try {
        setAddressesLoading(true);
        const res = await AddressesAPI.getAddresses();
        const addrs = res.addresses || [];
        setSavedAddresses(addrs);
        const def = addrs.find((a) => a.isDefault) || addrs[0] || null;
        if (def) setSelectedAddress(String(def.id));
      } catch (e) {
        if (e.message !== 'NOT_AUTHENTICATED') {
          toast.error('Adresler yüklenemedi');
        }
      } finally {
        setAddressesLoading(false);
      }
    };
    load();
  }, [isAuthenticated]);

  useEffect(() => {
    const loadCities = async () => {
      if (!isAuthenticated) return;
      try {
        const res = await LocationsAPI.getCities();
        setCities(res.cities || []);
      } catch {}
    };
    loadCities();
  }, [isAuthenticated]);

  useEffect(() => {
    const loadSuggested = async () => {
      if (!isAuthenticated) return;
      if (selectedAddress !== 'new') return;

      const hasLat = typeof newAddress.latitude === 'number' && Number.isFinite(newAddress.latitude);
      const hasLng = typeof newAddress.longitude === 'number' && Number.isFinite(newAddress.longitude);
      if (hasLat && hasLng) return;

      try {
        if (navigator.geolocation) {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 12000,
              maximumAge: 30000,
            });
          });
          const lat = Number(pos?.coords?.latitude);
          const lng = Number(pos?.coords?.longitude);
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            setNewAddress((p) => {
              const curHasLat = typeof p.latitude === 'number' && Number.isFinite(p.latitude);
              const curHasLng = typeof p.longitude === 'number' && Number.isFinite(p.longitude);
              if (curHasLat && curHasLng) return p;
              return { ...p, latitude: lat, longitude: lng };
            });
            return;
          }
        }
      } catch {
        // ignore
      }

      try {
        const res = await AddressesAPI.suggestLocation();
        const lat = Number(res?.latitude);
        const lng = Number(res?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        setNewAddress((p) => {
          const curHasLat = typeof p.latitude === 'number' && Number.isFinite(p.latitude);
          const curHasLng = typeof p.longitude === 'number' && Number.isFinite(p.longitude);
          if (curHasLat && curHasLng) return p;
          return { ...p, latitude: lat, longitude: lng };
        });
      } catch {
        // ignore
      }
    };

    loadSuggested();
  }, [isAuthenticated, selectedAddress, newAddress.latitude, newAddress.longitude]);

  useEffect(() => {
    const loadDistricts = async () => {
      if (!newAddress.city_id) {
        setDistricts([]);
        setNeighborhoods([]);
        return;
      }
      try {
        const res = await LocationsAPI.getDistricts(newAddress.city_id);
        setDistricts(res.districts || []);
      } catch {
        setDistricts([]);
      }
      setNeighborhoods([]);
      setNewAddress((prev) => ({
        ...prev,
        district_id: '',
        district_name: '',
        neighborhood_id: '',
        neighborhood_name: ''
      }));
    };
    loadDistricts();
  }, [newAddress.city_id]);

  useEffect(() => {
    const loadNeighborhoods = async () => {
      if (!newAddress.district_id) {
        setNeighborhoods([]);
        return;
      }
      try {
        const res = await LocationsAPI.getNeighborhoods(newAddress.district_id);
        setNeighborhoods(res.neighborhoods || []);
      } catch {
        setNeighborhoods([]);
      }
      setNewAddress((prev) => ({
        ...prev,
        neighborhood_id: '',
        neighborhood_name: ''
      }));
    };
    loadNeighborhoods();
  }, [newAddress.district_id]);

  const handleCreateAddress = async () => {
    if (!newAddress.title || !newAddress.name || !newAddress.phone || !newAddress.city_id || !newAddress.district_id || !newAddress.neighborhood_id || !newAddress.address_line) {
      toast.error('Lütfen tüm adres alanlarını doldurun');
      return;
    }
    try {
      const res = await AddressesAPI.createAddress({
        ...newAddress,
        is_default: savedAddresses.length === 0,
      });
      const created = res.address;
      const listRes = await AddressesAPI.getAddresses();
      setSavedAddresses(listRes.addresses || []);
      if (created?.id) {
        setSelectedAddress(String(created.id));
      }
      toast.success('Adres kaydedildi');
    } catch (e) {
      if (e.message === 'NOT_AUTHENTICATED') {
        toast.error('Lütfen giriş yapın');
        navigate('/giris');
      } else {
        toast.error(e.message || 'Adres kaydedilemedi');
      }
    }
  };

  const subtotal = getCartTotal();
  const shipping = getShippingCost();
  const total = getGrandTotal();

  if (cartItems.length === 0) {
    navigate('/sepetim');
    return null;
  }

  const handleProcessPayment = async () => {
    console.log('💳 Processing payment...');
    setPaymentProcessing(true);

    try {
      // SECURITY: Mock payment gateway for non-credit card payments
      // Credit card payments use iyzico iframe
      const headers = await getAuthHeaders({
        'Content-Type': 'application/json'
      });
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/payment/process`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          amount: total,
          currency: 'TRY',
          payment_method: paymentMethod
        })
      });

      const result = await response.json();
      console.log('💳 Payment result:', result);

      if (result.success) {
        console.log('✅ Payment successful:', result.payment_id);
        setPaymentId(result.payment_id);
        return result.payment_id;
      } else {
        console.error('❌ Payment failed:', result.error);
        toast.error('Ödeme başarısız: ' + result.error);
        return null;
      }
    } catch (error) {
      console.error('❌ Payment error:', error);
      toast.error('Ödeme işlemi sırasında hata oluştu');
      return null;
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleCompleteOrder = async () => {
    // Validate
    if (step === 1) {
      if (!selectedAddress && !newAddress.name) {
        toast.error('Lütfen teslimat adresi seçin veya ekleyin');
        return;
      }
      setStep(2);
      window.scrollTo(0, 0);
      return;
    }

    if (step === 2) {
      console.log('🔒 Step 2: Processing payment and creating order');
      console.log('   Payment method:', paymentMethod);
      console.log('   Payment ID:', paymentId);
      console.log('   User:', user);
      console.log('   Cart items:', cartItems);

      if (paymentMethod === 'transfer' || paymentMethod === 'door') {
        toast.info('yakında istediğiniz entegrasyon eklenecek');
        return;
      }

      // SECURITY: For credit card, open iyzico payment modal
      if (paymentMethod === 'credit-card' && !paymentId) {
        console.log('💳 Credit card payment - opening iyzico modal...');
        console.log('   Selected address:', selectedAddress);
        console.log('   Saved addresses:', savedAddresses);
        console.log('   New address:', newAddress);
        
        // Prepare order data for iyzico
        const address = selectedAddress === 'new' ? null : selectedSavedAddress;
        const shippingAddressPayload = selectedAddress === 'new'
          ? null
          : {
              name: address?.name,
              phone: address?.phone,
              address: address?.address,
              city: address?.city,
              district: address?.district || 'Merkez',
              title: address?.title || 'Ev',
              neighborhood: address?.neighborhood,
              latitude: typeof address?.latitude === 'number' ? address.latitude : null,
              longitude: typeof address?.longitude === 'number' ? address.longitude : null,
            };

        if (!shippingAddressPayload) {
          toast.error('Lütfen teslimat adresi seçin');
          setStep(1);
          return;
        }
        const orderData = {
          order_id: `ORD-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
          amount: total,
          items: cartItems.map(item => ({
            product_id: String(item.id),
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            category: item.category || 'Genel'
          })),
          shipping_address: {
            name: shippingAddressPayload.name,
            phone: shippingAddressPayload.phone,
            address: shippingAddressPayload.address,
            city: shippingAddressPayload.city,
            district: shippingAddressPayload.district,
            latitude: shippingAddressPayload.latitude,
            longitude: shippingAddressPayload.longitude,
            zip_code: '34000'
          },
          user_info: {
            id: user.id,
            name: user.name || shippingAddressPayload.name,
            email: user.email || `user${user.id}@example.com`,
            phone: shippingAddressPayload.phone
          },
          // Store cart and address data for order creation after payment
          cart_items: cartItems.map(item => ({
            product_id: String(item.id),
            quantity: item.quantity
          })),
          shipping_address_data: shippingAddressPayload
        };
        
        setIyzicoOrderData(orderData);
        setShowIyzicoModal(true);
        return;
      }

      // For other payment methods, process mock payment
      let processedPaymentId = paymentId;
      if (paymentMethod !== 'credit-card' && !paymentId) {
        console.log('💳 Processing non-credit card payment...');
        processedPaymentId = await handleProcessPayment();

        if (!processedPaymentId) {
          console.error('❌ Payment failed, aborting order creation');
          return;
        }
      }

      console.log('✅ Payment processed, creating order...');

      // SECURITY: Send minimal data to backend
      // Backend will fetch product details and prices from Sanity
      const orderData = {
        items: cartItems.map(item => ({
          product_id: String(item.id),
          quantity: item.quantity
          // product_name, product_price, product_image removed
          // Backend fetches these from Sanity for security
        })),
        shipping_address: selectedAddress === 'new' ? null : selectedSavedAddress,
        payment_method: paymentMethod,
        payment_id: processedPaymentId  // SECURITY: Include payment ID
      };

      if (!orderData.shipping_address) {
        toast.error('Lütfen teslimat adresi seçin');
        setStep(1);
        return;
      }

      console.log('📦 Order data:', { ...orderData, payment_id: processedPaymentId ? '✅ Present' : '❌ Missing' });

      try {
        // Create order via backend API
        console.log('🚀 Sending order to backend...');
        const response = await OrdersAPI.createOrder(orderData);
        console.log('✅ Order created successfully:', response);

        toast.success('Siparişiniz başarıyla oluşturuldu!');

        // Clear cart and redirect
        setTimeout(() => {
          clearCart();
          navigate('/siparislerim');
        }, 1500);
      } catch (error) {
        console.error('❌ Order creation failed:', error);
        if (error.message === 'NOT_AUTHENTICATED') {
          toast.error('Lütfen giriş yapın');
          navigate('/giris');
        } else {
          toast.error('Sipariş oluşturulurken hata oluştu. Lütfen tekrar deneyin.');
        }
      }
    }
  };

  return (
    <>
      <IyzicoPaymentModal
        isOpen={showIyzicoModal}
        onClose={() => setShowIyzicoModal(false)}
        orderData={iyzicoOrderData}
        userId={user?.id}
      />
      
      <div className="min-h-screen bg-gray-50 py-6 md:py-8" data-testid="checkout-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-4 md:gap-8">
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${step >= 1 ? 'bg-trendyol-orange text-white' : 'bg-gray-300 text-gray-600'}`}>
                {step > 1 ? <Check className="w-5 h-5" /> : '1'}
              </div>
              <span className="ml-2 text-sm font-medium hidden md:inline">Teslimat Adresi</span>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${step >= 2 ? 'bg-trendyol-orange text-white' : 'bg-gray-300 text-gray-600'}`}>
                {step > 2 ? <Check className="w-5 h-5" /> : '2'}
              </div>
              <span className="ml-2 text-sm font-medium hidden md:inline">Ödeme</span>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {/* Step 1: Address */}
            {step === 1 && (
              <div className="bg-white rounded-xl p-6">
                <h2 className="font-manrope font-bold text-xl mb-6">Teslimat Adresi</h2>

                {addressesLoading ? (
                  <div className="bg-white rounded-xl p-12 text-center">
                    <p className="text-gray-600">Yüklenıyor...</p>
                  </div>
                ) : savedAddresses.map((address) => (
                  <div
                    key={address.id}
                    onClick={() => setSelectedAddress(address.id)}
                    className={`border-2 rounded-lg p-4 mb-4 cursor-pointer transition-all ${selectedAddress === address.id ? 'border-trendyol-orange bg-orange-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    data-testid={`address-${address.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 mt-1 flex items-center justify-center ${selectedAddress === address.id ? 'border-trendyol-orange' : 'border-gray-300'
                        }`}>
                        {selectedAddress === address.id && (
                          <div className="w-3 h-3 rounded-full bg-trendyol-orange"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-bold">{address.name}</span>
                          <span className="text-xs bg-gray-100 px-2 py-1 rounded">{address.title}</span>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">{address.address}</p>
                        <p className="text-sm text-gray-600">{address.district} / {address.city}</p>
                        <p className="text-sm text-gray-600 mt-1">{address.phone}</p>
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  onClick={() => setSelectedAddress('new')}
                  className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-trendyol-orange font-medium hover:border-trendyol-orange hover:bg-orange-50 transition-all"
                  data-testid="add-new-address"
                >
                  + Yeni Adres Ekle
                </button>

                {selectedAddress === 'new' && (
                  <div className="mt-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Adres Başlığı</label>
                        <input
                          type="text"
                          value={newAddress.title}
                          onChange={(e) => setNewAddress({ ...newAddress, title: e.target.value })}
                          className="w-full border-2 border-gray-200 rounded-lg px-4 py-2 focus:border-trendyol-orange focus:outline-none"
                          placeholder="Ev, İş, vb."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Ad Soyad</label>
                        <input
                          type="text"
                          value={newAddress.name}
                          onChange={(e) => setNewAddress({ ...newAddress, name: e.target.value })}
                          className="w-full border-2 border-gray-200 rounded-lg px-4 py-2 focus:border-trendyol-orange focus:outline-none"
                          placeholder="Ad Soyad"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Telefon</label>
                        <input
                          type="tel"
                          value={newAddress.phone}
                          onChange={(e) => setNewAddress({ ...newAddress, phone: e.target.value })}
                          className="w-full border-2 border-gray-200 rounded-lg px-4 py-2 focus:border-trendyol-orange focus:outline-none"
                          placeholder="0532 123 45 67"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">İl</label>
                        <select
                          value={newAddress.city_id}
                          onChange={(e) => {
                            const id = e.target.value;
                            const city = cities.find((c) => String(c.id) === String(id));
                            setNewAddress({
                              ...newAddress,
                              city_id: id,
                              city_name: city?.name || '',
                            });
                          }}
                          className="w-full border-2 border-gray-200 rounded-lg px-4 py-2 focus:border-trendyol-orange focus:outline-none"
                        >
                          <option value="">Seçiniz</option>
                          {cities.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">İlçe</label>
                        <select
                          value={newAddress.district_id}
                          onChange={(e) => {
                            const id = e.target.value;
                            const d = districts.find((x) => String(x.id) === String(id));
                            setNewAddress({
                              ...newAddress,
                              district_id: id,
                              district_name: d?.name || '',
                            });
                          }}
                          className="w-full border-2 border-gray-200 rounded-lg px-4 py-2 focus:border-trendyol-orange focus:outline-none"
                          disabled={!newAddress.city_id}
                        >
                          <option value="">Seçiniz</option>
                          {districts.map((d) => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Mahalle</label>
                        <select
                          value={newAddress.neighborhood_id}
                          onChange={(e) => {
                            const id = e.target.value;
                            const n = neighborhoods.find((x) => String(x.id) === String(id));
                            setNewAddress({
                              ...newAddress,
                              neighborhood_id: id,
                              neighborhood_name: n?.name || '',
                            });
                          }}
                          className="w-full border-2 border-gray-200 rounded-lg px-4 py-2 focus:border-trendyol-orange focus:outline-none"
                          disabled={!newAddress.district_id}
                        >
                          <option value="">Seçiniz</option>
                          {neighborhoods.map((n) => (
                            <option key={n.id} value={n.id}>{n.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Adres</label>
                      <textarea
                        value={newAddress.address_line}
                        onChange={(e) => setNewAddress({ ...newAddress, address_line: e.target.value })}
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-2 focus:border-trendyol-orange focus:outline-none"
                        rows="3"
                        placeholder="Sokak, mahalle, bina no, daire no"
                      />
                    </div>
                    <div>
                      <LocationPickerMap
                        label="Konum (Haritadan Seç)"
                        value={{
                          lat: typeof newAddress.latitude === 'number' ? newAddress.latitude : null,
                          lng: typeof newAddress.longitude === 'number' ? newAddress.longitude : null,
                        }}
                        onChange={({ lat, lng }) => setNewAddress({ ...newAddress, latitude: lat, longitude: lng })}
                      />
                      <div className="grid grid-cols-2 gap-4 mt-3">
                        <div>
                          <label className="block text-sm font-medium mb-2">Enlem (lat)</label>
                          <input
                            type="number"
                            value={newAddress.latitude ?? ''}
                            onChange={(e) => setNewAddress({ ...newAddress, latitude: e.target.value === '' ? null : Number(e.target.value) })}
                            className="w-full border-2 border-gray-200 rounded-lg px-4 py-2 focus:border-trendyol-orange focus:outline-none"
                            step="any"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Boylam (lng)</label>
                          <input
                            type="number"
                            value={newAddress.longitude ?? ''}
                            onChange={(e) => setNewAddress({ ...newAddress, longitude: e.target.value === '' ? null : Number(e.target.value) })}
                            className="w-full border-2 border-gray-200 rounded-lg px-4 py-2 focus:border-trendyol-orange focus:outline-none"
                            step="any"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mt-2">Haritaya tıklayarak pin bırakabilirsin. Koordinat girmezsen adres yine kaydedilir.</p>
                    </div>
                    <div className="flex justify-end">
                      <button
                        onClick={handleCreateAddress}
                        className="bg-trendyol-orange hover:bg-orange-600 text-white font-semibold px-6 py-2 rounded-lg transition-colors"
                      >
                        Adresi Kaydet
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Payment */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-6">
                  <h2 className="font-manrope font-bold text-xl mb-6">Ödeme Yöntemi</h2>

                  <div className="space-y-3 mb-6">
                    <div
                      onClick={() => setPaymentMethod('credit-card')}
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${paymentMethod === 'credit-card' ? 'border-trendyol-orange bg-orange-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      data-testid="payment-credit-card"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'credit-card' ? 'border-trendyol-orange' : 'border-gray-300'
                          }`}>
                          {paymentMethod === 'credit-card' && (
                            <div className="w-3 h-3 rounded-full bg-trendyol-orange"></div>
                          )}
                        </div>
                        <CreditCard className="w-5 h-5 text-gray-600" />
                        <span className="font-medium">Kredi / Banka Kartı</span>
                      </div>
                    </div>

                    <div
                      onClick={() => setPaymentMethod('transfer')}
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${paymentMethod === 'transfer' ? 'border-trendyol-orange bg-orange-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      data-testid="payment-transfer"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'transfer' ? 'border-trendyol-orange' : 'border-gray-300'
                          }`}>
                          {paymentMethod === 'transfer' && (
                            <div className="w-3 h-3 rounded-full bg-trendyol-orange"></div>
                          )}
                        </div>
                        <Landmark className="w-5 h-5 text-gray-600" />
                        <span className="font-medium">Havale / EFT</span>
                      </div>
                    </div>

                    <div
                      onClick={() => setPaymentMethod('door')}
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${paymentMethod === 'door' ? 'border-trendyol-orange bg-orange-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      data-testid="payment-door"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'door' ? 'border-trendyol-orange' : 'border-gray-300'
                          }`}>
                          {paymentMethod === 'door' && (
                            <div className="w-3 h-3 rounded-full bg-trendyol-orange"></div>
                          )}
                        </div>
                        <Wallet className="w-5 h-5 text-gray-600" />
                        <span className="font-medium">Kapıda Ödeme</span>
                      </div>
                    </div>
                  </div>

                  {paymentMethod === 'credit-card' && (
                    <div className="space-y-4 mt-6 pt-6 border-t border-gray-200">
                      <div className="bg-blue-50 p-6 rounded-lg text-center">
                        <Lock className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                        <h4 className="font-semibold text-lg mb-2">Güvenli Ödeme</h4>
                        <p className="text-sm text-gray-600 mb-4">
                          Kart bilgileriniz güvenli ödeme gateway'i üzerinden işlenecektir.
                        </p>
                        <p className="text-xs text-gray-500">
                          🔒 PCI DSS Uyumlu | 3D Secure Korumalı
                        </p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <div className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-gray-700">
                            <p className="font-medium mb-1">Güvenlik Önlemleri:</p>
                            <ul className="space-y-1 text-xs">
                              <li>✓ Kart bilgileriniz şifrelenir</li>
                              <li>✓ Hiçbir kart bilgisi saklanmaz</li>
                              <li>✓ 3D Secure ile doğrulanır</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {paymentMethod === 'transfer' && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2">Havale Bilgileri</h4>
                        <p className="text-sm text-gray-600">yakında istediğiniz entegrasyon eklenecek</p>
                      </div>
                    </div>
                  )}

                  {paymentMethod === 'door' && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <div className="bg-yellow-50 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2">Kapıda Ödeme</h4>
                        <p className="text-sm text-gray-600">yakında istediğiniz entegrasyon eklenecek</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Order Summary - Right Side */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 sticky top-24">
              <h3 className="font-semibold text-lg mb-4">Sipariş Özeti</h3>

              <div className="space-y-3 mb-4">
                {cartItems.slice(0, 3).map((item) => (
                  <div key={item.id}>
                    <div className="flex gap-3">
                      <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium line-clamp-1">{item.name}</p>
                        <p className="text-xs text-gray-500">{item.quantity} adet</p>
                        <p className="text-sm font-semibold text-trendyol-orange">{(item.price * item.quantity).toFixed(2)} TL</p>
                      </div>
                    </div>

                    {Array.isArray(item.extras) && item.extras.length > 0 && (
                      <div className="mt-2 ml-1 rounded-lg border border-gray-200 bg-gray-50 p-2">
                        <div className="text-xs font-semibold text-gray-700">Ekstralar</div>
                        <div className="mt-1 space-y-1">
                          {item.extras.map((ex) => (
                            <div key={String(ex.extra_id)} className="flex items-center justify-between gap-3">
                              <div className="text-xs text-gray-700 truncate">{ex.extra_name || 'Ekstra'}</div>
                              <div className="text-right">
                                <div className="text-xs font-semibold text-gray-900 whitespace-nowrap">
                                  {Number(ex.quantity) || 1} adet · {((Number(ex.extra_price) || 0) * (Number(ex.quantity) || 1)).toFixed(2)} TL
                                </div>
                                <div className="text-[11px] text-gray-500 whitespace-nowrap">
                                  {(Number(ex.extra_price) || 0).toFixed(2)} TL / adet
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {cartItems.length > 3 && (
                  <p className="text-sm text-gray-500">ve {cartItems.length - 3} ürün daha...</p>
                )}
              </div>

              <div className="space-y-3 mb-4 pb-4 border-t border-b border-gray-200 pt-4">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Ürün Toplamı</span>
                  <span>{subtotal.toFixed(2)} TL</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Kargo Toplamı</span>
                  <span className={shipping === 0 ? 'text-green-600 font-medium' : ''}>
                    {shipping === 0 ? 'ÜCRETSİZ' : `${shipping.toFixed(2)} TL`}
                  </span>
                </div>
              </div>

              <div className="flex justify-between font-manrope font-bold text-xl mb-6">
                <span>Toplam</span>
                <span className="text-trendyol-orange">{total.toFixed(2)} TL</span>
              </div>

              {step === 1 && (
                <button
                  onClick={handleCompleteOrder}
                  className="w-full bg-trendyol-orange hover:bg-orange-600 text-white font-semibold py-3 rounded-lg transition-colors"
                  data-testid="continue-to-payment"
                >
                  Ödeme Adımına Geç
                </button>
              )}

              {step === 2 && (
                <button
                  onClick={handleCompleteOrder}
                  className="w-full bg-trendyol-orange hover:bg-orange-600 text-white font-semibold py-3 rounded-lg transition-colors"
                  data-testid="complete-order"
                >
                  Siparişi Tamamla
                </button>
              )}

              <button
                onClick={() => {
                  if (step === 2) {
                    setStep(1);
                  } else {
                    navigate('/sepetim');
                  }
                }}
                className="w-full text-center text-trendyol-orange hover:underline font-medium mt-3"
                data-testid="go-back"
              >
                {step === 2 ? 'Adres Seçimine Dön' : 'Sepete Dön'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
